import './BasicInfoFormModal.scss';
import { FormController } from '@form-ts/core';
import { useForm } from '@form-ts/react';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'src/modules/common/components/Button';
import { ModalBody } from 'src/modules/common/components/ModalBody';
import { ModalFooter } from 'src/modules/common/components/ModalFooter';
import { ModalHeader } from 'src/modules/common/components/ModalHeader';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { FileField } from 'src/modules/form/components/FileField';
import { TextAreaField } from 'src/modules/form/components/TextAreaField';
import { TextField } from 'src/modules/form/components/TextField';
import { useFormValidator } from 'src/modules/form/hooks/useFormValidator';
import { FormError } from 'src/modules/form/types/FormError';
import { BASIC_SCHEMA } from 'src/modules/project-new/constants/schema';
import { BasicFormData } from 'src/modules/project-new/types/BasicFormData';

type Props = {
  readonly formData: BasicFormData;
  readonly onNext: (form: FormController<BasicFormData, FormError>) => void;
  readonly onClose: () => void;
};

export const BasicInfoFormModal = ({ formData, onNext, onClose }: Props): React.ReactElement => {
  const intl = useIntl();

  const form = useForm<BasicFormData, FormError>('project.create.new.basic', {
    reinitialize: false,
    initialValues: formData,
  });
  useFormValidator(form, BASIC_SCHEMA);

  const handleSubmit = useCallback((): void => {
    onNext(form);
  }, [form, onNext]);

  return (
    <>
      <ModalHeader
        label={intl.formatMessage({ id: 'project/create/basic/title' })}
        title={intl.formatMessage({ id: 'project/create/basic/subTitle' })}
        handleClose={onClose}
      />

      <ModalBody>
        <div className="bp-project-create-modal__body">
          <TextField
            size="lg"
            field={form.field.at('title')}
            label={intl.formatMessage({ id: 'project/create/building/field/title' })}
            required={true}
            maxLength={40}
            autoFocus={true}
            width="100%"
          />

          <TextAreaField
            field={form.field.at('projectGoals')}
            label={intl.formatMessage({ id: 'basedata/projectGoals' })}
            rows={2}
            theme="gray"
            maxLength={280}
          />

          <div className="bp-basic-info-form-modal__file-field">
            <label
              className="bp-basic-info-form-modal__file-label"
              htmlFor={`${form.field.at('cover').form.name}.${form.field.at('cover').path}`}
              aria-label={intl.formatMessage({ id: 'basedata/coverImage' })}
            >
              <FormattedMessage id="basedata/coverImage"/>
            </label>

            <FileField
              className="bp-basic-info-form-modal__file-image-container"
              field={form.field.at('cover')}
            />
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          className="bp-project-create-modal__button bp-project-create-modal__button--secondary"
          kind="secondary"
          fullWidth={true}
          onClick={onClose}
          data-test-id="back-to-project-list-button"
        >
          <FormattedMessage id="project/create/action/back"/>
        </Button>

        <Button
          className="bp-project-create-modal__button bp-project-create-modal__button--primary"
          kind="primary"
          fullWidth={true}
          onClick={handleSubmit}
          data-test-id="next-step-button"
        >
          <FormattedMessage id="project/create/action/next"/>

          <div className="bp-project-create-modal__button-icon">
            <ArrowRightIcon/>
          </div>
        </Button>
      </ModalFooter>
    </>
  );
};
